import { SeededRandom } from "../core/random";
import type { GameHud, GameInstance, GameServices } from "../core/types";
import { drawPanel, drawScanlines, roundedRectPath, setText } from "./drawing";

const ROUND_SECONDS = 60;
const ORIGIN_INTEGRITY = 10;
const TOWER_COST = 3;
const TOWER_RANGE = 168;
const TOWER_COOLDOWN = 0.5;

interface Point {
  x: number;
  y: number;
}

interface Enemy {
  dist: number;
  hp: number;
  maxHp: number;
  speed: number;
}

interface Tower {
  x: number;
  y: number;
  cooldown: number;
  flash: number;
}

const PATH: readonly Point[] = [
  { x: -40, y: 210 },
  { x: 330, y: 210 },
  { x: 330, y: 520 },
  { x: 770, y: 520 },
  { x: 770, y: 230 },
  { x: 1130, y: 230 },
  { x: 1210, y: 230 },
];

const SLOTS: readonly Point[] = [
  { x: 210, y: 340 },
  { x: 450, y: 380 },
  { x: 470, y: 640 },
  { x: 650, y: 380 },
  { x: 650, y: 650 },
  { x: 900, y: 360 },
  { x: 900, y: 120 },
  { x: 1080, y: 350 },
];

export class DdosBastionGame implements GameInstance {
  private readonly random = new SeededRandom(Date.now() ^ 0xdd05);
  private readonly segLengths: number[] = [];
  private readonly totalLength: number;
  private readonly enemies: Enemy[] = [];
  private readonly towers: (Tower | null)[] = Array<Tower | null>(SLOTS.length).fill(null);
  private focus = 0;
  private budget = 4;
  private score = 0;
  private time = ROUND_SECONDS;
  private elapsed = 0;
  private spawnTimer = 1;
  private moveCooldown = 0;
  private integrity = ORIGIN_INTEGRITY;
  private message = "Place firewalls. Absorb the flood before it reaches origin.";
  private messageTimer = 0;
  private ended = false;

  constructor(private readonly services: GameServices) {
    let total = 0;
    for (let index = 0; index < PATH.length - 1; index += 1) {
      const a = PATH[index];
      const b = PATH[index + 1];
      if (!a || !b) continue;
      const length = Math.hypot(b.x - a.x, b.y - a.y);
      this.segLengths.push(length);
      total += length;
    }
    this.totalLength = total;
  }

  advanceTime(deltaSeconds: number): void {
    if (this.ended) return;
    this.elapsed += deltaSeconds;
    this.time = Math.max(0, ROUND_SECONDS - this.elapsed);
  }

  update(deltaSeconds: number): void {
    if (this.ended) return;

    this.messageTimer = Math.max(0, this.messageTimer - deltaSeconds);
    this.moveCooldown = Math.max(0, this.moveCooldown - deltaSeconds);
    if (this.messageTimer === 0) this.message = "Place firewalls. Absorb the flood before it reaches origin.";

    if (this.time === 0) {
      this.finish("Origin held");
      return;
    }

    this.updateCursor();
    if (this.services.input.consumePressed("primary")) this.buildFocused();
    this.updateEnemies(deltaSeconds);
    if (this.ended) return;
    this.updateTowers(deltaSeconds);
  }

  render(context: CanvasRenderingContext2D): void {
    const background = context.createLinearGradient(0, 0, 1280, 720);
    background.addColorStop(0, "#0a1020");
    background.addColorStop(0.6, "#0c1c2e");
    background.addColorStop(1, "#070c16");
    context.fillStyle = background;
    context.fillRect(0, 0, 1280, 720);
    drawScanlines(context);

    this.drawPath(context);
    this.drawOrigin(context);
    this.drawSlots(context);
    for (const enemy of this.enemies) this.drawEnemy(context, enemy);
    this.drawTowers(context);
    this.drawHeader(context);
    this.drawFooter(context);
  }

  getHud(): GameHud {
    return { score: this.score, time: this.time, message: this.message, ended: this.ended };
  }

  destroy(): void {}

  private pointAt(dist: number): Point {
    let remaining = Math.max(0, dist);
    for (let index = 0; index < this.segLengths.length; index += 1) {
      const length = this.segLengths[index] ?? 0;
      const a = PATH[index];
      const b = PATH[index + 1];
      if (!a || !b) continue;
      if (remaining <= length || index === this.segLengths.length - 1) {
        const t = length === 0 ? 0 : Math.min(1, remaining / length);
        return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
      }
      remaining -= length;
    }
    const last = PATH[PATH.length - 1] ?? { x: 0, y: 0 };
    return { x: last.x, y: last.y };
  }

  private updateCursor(): void {
    const input = this.services.input;
    let direction = 0;
    if (input.consumePressed("right") || input.consumePressed("down")) direction = 1;
    else if (input.consumePressed("left") || input.consumePressed("up")) direction = -1;
    else if (this.moveCooldown === 0) {
      if (input.isDown("right") || input.isDown("down")) direction = 1;
      else if (input.isDown("left") || input.isDown("up")) direction = -1;
    }
    if (direction === 0) return;
    this.focus = (this.focus + direction + SLOTS.length) % SLOTS.length;
    this.moveCooldown = 0.16;
    this.services.audio.play("move");
  }

  private buildFocused(): void {
    if (this.towers[this.focus]) {
      this.setMessage("Slot already defended", 1.1);
      return;
    }
    if (this.budget < TOWER_COST) {
      this.services.audio.play("miss");
      this.setMessage("Not enough capacity - clear attackers to earn more", 1.6);
      return;
    }
    const slot = SLOTS[this.focus];
    if (!slot) return;
    this.budget -= TOWER_COST;
    this.towers[this.focus] = { x: slot.x, y: slot.y, cooldown: 0, flash: 0 };
    this.services.audio.play("confirm");
    this.setMessage("Firewall online", 1.2);
  }

  private updateEnemies(deltaSeconds: number): void {
    this.spawnTimer -= deltaSeconds;
    if (this.spawnTimer <= 0) {
      const hp = 2 + Math.floor(this.elapsed / 11);
      this.enemies.push({ dist: 0, hp, maxHp: hp, speed: this.random.between(74, 104) });
      this.spawnTimer = Math.max(0.5, 1.35 - this.elapsed * 0.013) + this.random.between(0, 0.25);
    }

    for (let index = this.enemies.length - 1; index >= 0; index -= 1) {
      const enemy = this.enemies[index];
      if (!enemy) continue;
      enemy.dist += enemy.speed * deltaSeconds;
      if (enemy.dist < this.totalLength) continue;
      this.enemies.splice(index, 1);
      this.integrity -= 1;
      this.services.audio.play("miss");
      if (this.integrity <= 0) {
        this.finish("Origin overwhelmed");
        return;
      }
      this.setMessage(`Attacker breached origin - ${this.integrity} integrity left`, 1.6);
    }
  }

  private updateTowers(deltaSeconds: number): void {
    for (const tower of this.towers) {
      if (!tower) continue;
      tower.cooldown = Math.max(0, tower.cooldown - deltaSeconds);
      tower.flash = Math.max(0, tower.flash - deltaSeconds);
      if (tower.cooldown > 0) continue;

      let targetIndex = -1;
      let bestDist = Number.POSITIVE_INFINITY;
      for (let index = 0; index < this.enemies.length; index += 1) {
        const enemy = this.enemies[index];
        if (!enemy) continue;
        const position = this.pointAt(enemy.dist);
        const range = Math.hypot(position.x - tower.x, position.y - tower.y);
        if (range <= TOWER_RANGE && range < bestDist) {
          bestDist = range;
          targetIndex = index;
        }
      }
      if (targetIndex < 0) continue;

      const enemy = this.enemies[targetIndex];
      if (!enemy) continue;
      tower.cooldown = TOWER_COOLDOWN;
      tower.flash = 0.12;
      enemy.hp -= 1;
      if (enemy.hp <= 0) {
        this.enemies.splice(targetIndex, 1);
        this.budget += 1;
        this.score += 60;
        this.services.audio.play("hit");
      }
    }
  }

  private setMessage(message: string, duration: number): void {
    this.message = message;
    this.messageTimer = duration;
    this.services.announce(message);
  }

  private finish(message: string): void {
    this.ended = true;
    this.message = message;
    this.services.audio.play("finish");
    this.services.announce(`${message}. Final score ${this.score}.`);
  }

  private drawPath(context: CanvasRenderingContext2D): void {
    context.save();
    context.strokeStyle = "rgba(255, 93, 93, 0.28)";
    context.lineWidth = 40;
    context.lineJoin = "round";
    context.lineCap = "round";
    context.beginPath();
    for (let index = 0; index < PATH.length; index += 1) {
      const point = PATH[index];
      if (!point) continue;
      if (index === 0) context.moveTo(point.x, point.y);
      else context.lineTo(point.x, point.y);
    }
    context.stroke();
    context.strokeStyle = "rgba(255, 122, 26, 0.5)";
    context.lineWidth = 2;
    context.setLineDash([12, 12]);
    context.stroke();
    context.setLineDash([]);
    context.restore();
  }

  private drawOrigin(context: CanvasRenderingContext2D): void {
    const origin = PATH[PATH.length - 1] ?? { x: 1210, y: 230 };
    context.save();
    context.shadowColor = "#42e2c3";
    context.shadowBlur = 22;
    context.fillStyle = "#0b2238";
    context.strokeStyle = "#42e2c3";
    context.lineWidth = 4;
    roundedRectPath(context, origin.x - 44, origin.y - 44, 88, 88, 14);
    context.fill();
    context.stroke();
    context.shadowBlur = 0;
    setText(context, 13, 900, "#9feddd", "center");
    context.fillText("ORIGIN", origin.x, origin.y);
    context.restore();
  }

  private drawSlots(context: CanvasRenderingContext2D): void {
    for (let index = 0; index < SLOTS.length; index += 1) {
      const slot = SLOTS[index];
      if (!slot) continue;
      if (this.towers[index]) continue;
      const focused = index === this.focus;
      context.save();
      context.strokeStyle = focused ? "#2fe3ff" : "rgba(159, 178, 194, 0.4)";
      context.lineWidth = focused ? 3 : 2;
      context.setLineDash(focused ? [] : [6, 6]);
      roundedRectPath(context, slot.x - 26, slot.y - 26, 52, 52, 10);
      context.stroke();
      if (focused) {
        setText(context, 11, 800, "#2fe3ff", "center");
        context.fillText("BUILD", slot.x, slot.y);
      }
      context.restore();
    }
  }

  private drawTowers(context: CanvasRenderingContext2D): void {
    for (let index = 0; index < this.towers.length; index += 1) {
      const tower = this.towers[index];
      if (!tower) continue;
      const focused = index === this.focus;
      context.save();
      context.shadowColor = "#2fe3ff";
      context.shadowBlur = tower.flash > 0 ? 26 : 12;
      context.fillStyle = "#0b2238";
      context.strokeStyle = focused ? "#9feddd" : "#2fe3ff";
      context.lineWidth = 3;
      roundedRectPath(context, tower.x - 24, tower.y - 24, 48, 48, 10);
      context.fill();
      context.stroke();
      context.shadowBlur = 0;
      setText(context, 12, 900, "#9feddd", "center");
      context.fillText("FW", tower.x, tower.y);
      if (tower.flash > 0) {
        context.strokeStyle = "rgba(47, 227, 255, 0.4)";
        context.lineWidth = 2;
        context.beginPath();
        context.arc(tower.x, tower.y, TOWER_RANGE, 0, Math.PI * 2);
        context.stroke();
      }
      context.restore();
    }
  }

  private drawEnemy(context: CanvasRenderingContext2D, enemy: Enemy): void {
    const position = this.pointAt(enemy.dist);
    context.save();
    context.translate(position.x, position.y);
    context.shadowColor = "#ff5d5d";
    context.shadowBlur = 14;
    context.fillStyle = "#2a0d14";
    context.strokeStyle = "#ff5d5d";
    context.lineWidth = 3;
    context.beginPath();
    for (let side = 0; side < 6; side += 1) {
      const angle = side * (Math.PI / 3);
      const px = Math.cos(angle) * 17;
      const py = Math.sin(angle) * 17;
      if (side === 0) context.moveTo(px, py);
      else context.lineTo(px, py);
    }
    context.closePath();
    context.fill();
    context.stroke();
    context.shadowBlur = 0;
    context.fillStyle = "rgba(255, 255, 255, 0.14)";
    context.fillRect(-17, -27, 34, 5);
    context.fillStyle = "#ff5d5d";
    context.fillRect(-17, -27, 34 * (enemy.hp / enemy.maxHp), 5);
    context.restore();
  }

  private drawHeader(context: CanvasRenderingContext2D): void {
    drawPanel(context, 42, 42, 340, 92, "#ff7a1a");
    setText(context, 12, 850, "#ffca8a");
    context.fillText("NETWORK FIREWALL", 72, 72);
    setText(context, 22, 900, "#f7fbff");
    context.fillText(`CAPACITY  ${this.budget}`, 72, 106);

    setText(context, 11, 850, "#839aa5", "right");
    context.fillText("ORIGIN INTEGRITY", 1232, 62);
    context.fillStyle = "rgba(255, 255, 255, 0.12)";
    roundedRectPath(context, 1012, 80, 220, 14, 7);
    context.fill();
    context.fillStyle = this.integrity > 3 ? "#42e2c3" : "#ff5d5d";
    roundedRectPath(context, 1012, 80, Math.max(3, 220 * (this.integrity / ORIGIN_INTEGRITY)), 14, 7);
    context.fill();
  }

  private drawFooter(context: CanvasRenderingContext2D): void {
    drawPanel(context, 42, 650, 1196, 54, "#ff7a1a");
    setText(context, 20, 850, "#f7fbff");
    context.fillText(this.message, 72, 678);
    setText(context, 12, 750, "#9cb2bd", "right");
    context.fillText("SELECT  \u2190 \u2192     PLACE  SPACE", 1204, 678);
  }
}
